import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { fetchProcesses, createProcess, updateProcess, deleteProcess, fetchProjects, fetchProcessById } from '../services/api';

const AddProcess = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProjectTimeline, setSelectedProjectTimeline] = useState(null);
  const [formData, setFormData] = useState({
    projectId: '',
    name: '',
    valueOfProcess: '',
    description: '',
    status: 'active',
    owner: '',
    start_date: '',
    end_date: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, [snackbar]);

  const loadProjects = useCallback(async () => {
    try {
      const projectsData = await fetchProjects();
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
      showSnackbar('Failed to load projects', 'error');
    }
  }, [showSnackbar]);

  const fetchProjectTimeline = useCallback(async (projectId) => {
    if (!projectId) {
      setSelectedProjectTimeline(null);
      return;
    }
    
    try {
      // Find the selected project
      const selectedProject = projects.flatMap(group => group.projects).find(p => p.id === projectId);
      if (!selectedProject) return;

      // Fetch project timeline from backend
      const response = await fetch(`/api/project-timelines?project_name=${encodeURIComponent(selectedProject.project_name)}`);
      if (response.ok) {
        const timelineData = await response.json();
        if (timelineData && timelineData.length > 0) {
          setSelectedProjectTimeline(timelineData[0]);
        } else {
          setSelectedProjectTimeline(null);
        }
      }
    } catch (error) {
      console.error('Error fetching project timeline:', error);
      setSelectedProjectTimeline(null);
    }
  }, [projects]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // If project is changed, fetch its timeline
    if (name === 'projectId') {
      fetchProjectTimeline(value);
    }
  }, [fetchProjectTimeline]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const validateTimeline = useCallback(() => {
    if (!formData.projectId || !formData.start_date || !formData.end_date) {
      return { isValid: true }; // No validation needed if not all fields are filled
    }

    if (!selectedProjectTimeline) {
      return { isValid: false, message: 'Project timeline not found' };
    }

    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);
    const projectStart = new Date(selectedProjectTimeline.start_date);
    const projectEnd = new Date(selectedProjectTimeline.end_date);

    if (startDate < projectStart || endDate > projectEnd) {
      return {
        isValid: false,
        message: `Process timeline must be within project timeline (${selectedProjectTimeline.start_date} to ${selectedProjectTimeline.end_date})`
      };
    }

    if (startDate >= endDate) {
      return {
        isValid: false,
        message: 'Process start date must be before end date'
      };
    }

    return { isValid: true };
  }, [formData, selectedProjectTimeline]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate timeline
    const timelineValidation = validateTimeline();
    if (!timelineValidation.isValid) {
      showSnackbar(timelineValidation.message, 'error');
      return;
    }

    try {
      if (!formData.name.trim()) {
        showSnackbar('Process name is required', 'error');
        return;
      }

      // Only create the process, do not create a timeline assignment
      await createProcess(formData);
      showSnackbar('Process created successfully');
      navigate('/processes/list', { replace: true });
    } catch (error) {
      console.error('Error creating process:', error);
      showSnackbar(error.message || 'Failed to create process', 'error');
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        Create New Process
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Project *</InputLabel>
                <Select
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleChange}
                  label="Project *"
                >
                  <MenuItem value="">
                    <em>Select a project</em>
                  </MenuItem>
                  {projects.map((group) => (
                    group.projects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {group.client_name} - {project.project_name}
                        {selectedProjectTimeline && selectedProjectTimeline.project_name === project.project_name && (
                          <span style={{ color: '#666', fontSize: '0.8em', marginLeft: '8px' }}>
                            ({selectedProjectTimeline.start_date} to {selectedProjectTimeline.end_date})
                          </span>
                        )}
                      </MenuItem>
                    ))
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {selectedProjectTimeline && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Project Timeline: {selectedProjectTimeline.start_date} to {selectedProjectTimeline.end_date}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: selectedProjectTimeline?.start_date || '',
                  max: selectedProjectTimeline?.end_date || ''
                }}
                helperText={selectedProjectTimeline ? `Must be between ${selectedProjectTimeline.start_date} and ${selectedProjectTimeline.end_date}` : ''}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: selectedProjectTimeline?.start_date || '',
                  max: selectedProjectTimeline?.end_date || ''
                }}
                helperText={selectedProjectTimeline ? `Must be between ${selectedProjectTimeline.start_date} and ${selectedProjectTimeline.end_date}` : ''}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Process Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                helperText="Process names must be unique"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Value of Process"
                name="valueOfProcess"
                value={formData.valueOfProcess}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Process Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Process Owner"
                name="owner"
                value={formData.owner}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/processes/list')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Create Process
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const EditProcess = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [projects, setProjects] = useState([]);
  const [selectedProjectTimeline, setSelectedProjectTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    projectId: '',
    name: '',
    valueOfProcess: '',
    description: '',
    status: 'active',
    owner: '',
    start_date: '',
    end_date: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, [snackbar]);

  const loadProjects = useCallback(async () => {
    try {
      const projectsData = await fetchProjects();
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
      showSnackbar('Failed to load projects', 'error');
    }
  }, [showSnackbar]);

  const fetchProjectTimeline = useCallback(async (projectId) => {
    if (!projectId) {
      setSelectedProjectTimeline(null);
      return;
    }
    
    try {
      // Find the selected project
      const selectedProject = projects.flatMap(group => group.projects).find(p => p.id === projectId);
      if (!selectedProject) return;

      // Fetch project timeline from backend
      const response = await fetch(`/api/project-timelines?project_name=${encodeURIComponent(selectedProject.project_name)}`);
      if (response.ok) {
        const timelineData = await response.json();
        if (timelineData && timelineData.length > 0) {
          setSelectedProjectTimeline(timelineData[0]);
        } else {
          setSelectedProjectTimeline(null);
        }
      }
    } catch (error) {
      console.error('Error fetching project timeline:', error);
      setSelectedProjectTimeline(null);
    }
  }, [projects]);

  // Fetch process data for editing
  const loadProcessData = useCallback(async () => {
    try {
      const processData = await fetchProcessById(id);
      setFormData({
        projectId: processData.project_id || '',
        name: processData.name || '',
        valueOfProcess: processData.value_of_process || '',
        description: processData.description || '',
        status: processData.status || 'active',
        owner: processData.owner || '',
        start_date: processData.start_date || '',
        end_date: processData.end_date || ''
      });
      setLoading(false);
      
      // Fetch timeline for the project if it exists
      if (processData.project_id) {
        fetchProjectTimeline(processData.project_id);
      }
    } catch (error) {
      console.error('Error loading process data:', error);
      showSnackbar('Failed to load process data', 'error');
      setLoading(false);
    }
  }, [id, showSnackbar, fetchProjectTimeline]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // If project is changed, fetch its timeline
    if (name === 'projectId') {
      fetchProjectTimeline(value);
    }
  }, [fetchProjectTimeline]);

  useEffect(() => {
    loadProjects();
    loadProcessData();
  }, [id, loadProjects, loadProcessData]);

  const validateTimeline = useCallback(() => {
    if (!formData.projectId || !formData.start_date || !formData.end_date) {
      return { isValid: true }; // No validation needed if not all fields are filled
    }

    if (!selectedProjectTimeline) {
      return { isValid: false, message: 'Project timeline not found' };
    }

    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);
    const projectStart = new Date(selectedProjectTimeline.start_date);
    const projectEnd = new Date(selectedProjectTimeline.end_date);

    if (startDate < projectStart || endDate > projectEnd) {
      return {
        isValid: false,
        message: `Process timeline must be within project timeline (${selectedProjectTimeline.start_date} to ${selectedProjectTimeline.end_date})`
      };
    }

    if (startDate >= endDate) {
      return {
        isValid: false,
        message: 'Process start date must be before end date'
      };
    }

    return { isValid: true };
  }, [formData, selectedProjectTimeline]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate timeline
    const timelineValidation = validateTimeline();
    if (!timelineValidation.isValid) {
      showSnackbar(timelineValidation.message, 'error');
      return;
    }

    try {
      await updateProcess(id, formData);
      showSnackbar('Process updated successfully');
      navigate('/processes/list', { replace: true });
    } catch (error) {
      console.error('Error updating process:', error);
      showSnackbar(error.message || 'Failed to update process', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        <Typography variant="h5" sx={{ mb: 4 }}>
          Edit Process
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography>Loading process data...</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        Edit Process
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Project *</InputLabel>
                <Select
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleChange}
                  label="Project *"
                >
                  <MenuItem value="">
                    <em>Select a project</em>
                  </MenuItem>
                  {projects.map((group) => (
                    group.projects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {group.client_name} - {project.project_name}
                        {selectedProjectTimeline && selectedProjectTimeline.project_name === project.project_name && (
                          <span style={{ color: '#666', fontSize: '0.8em', marginLeft: '8px' }}>
                            ({selectedProjectTimeline.start_date} to {selectedProjectTimeline.end_date})
                          </span>
                        )}
                      </MenuItem>
                    ))
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {selectedProjectTimeline && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Project Timeline: {selectedProjectTimeline.start_date} to {selectedProjectTimeline.end_date}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: selectedProjectTimeline?.start_date || '',
                  max: selectedProjectTimeline?.end_date || ''
                }}
                helperText={selectedProjectTimeline ? `Must be between ${selectedProjectTimeline.start_date} and ${selectedProjectTimeline.end_date}` : ''}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: selectedProjectTimeline?.start_date || '',
                  max: selectedProjectTimeline?.end_date || ''
                }}
                helperText={selectedProjectTimeline ? `Must be between ${selectedProjectTimeline.start_date} and ${selectedProjectTimeline.end_date}` : ''}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Process Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                helperText="Process names must be unique"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Value of Process"
                name="valueOfProcess"
                value={formData.valueOfProcess}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Process Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Process Owner"
                name="owner"
                value={formData.owner}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/processes/list')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Update Process
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const ProcessList = () => {
  const navigate = useNavigate();
  const [processes, setProcesses] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, [snackbar]);

  const loadProcesses = useCallback(async () => {
    try {
      const data = await fetchProcesses();
      setProcesses(data);
    } catch (error) {
      console.error('Error loading processes:', error);
      showSnackbar('Failed to load processes', 'error');
    }
  }, [showSnackbar]);

  useEffect(() => {
    loadProcesses();
  }, [loadProcesses]);

  const handleDelete = async (processId) => {
    if (window.confirm('Are you sure you want to delete this process?')) {
      try {
        await deleteProcess(processId);
        showSnackbar('Process deleted successfully');
        loadProcesses();
      } catch (error) {
        console.error('Error deleting process:', error);
        showSnackbar(error.message || 'Failed to delete process', 'error');
      }
    }
  };

  // Group processes by client and project
  const grouped = {};
  processes.forEach((process) => {
    const client = process.company_name || 'No Client';
    const project = process.project_name || 'No Project';
    if (!grouped[client]) grouped[client] = {};
    if (!grouped[client][project]) grouped[client][project] = [];
    grouped[client][project].push(process);
  });

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Processes</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/processes/add')}
        >
          Add New Process
        </Button>
      </Box>

      {/* Grouped display by client and project */}
      {Object.entries(grouped).map(([client, projects]) => (
        <Paper key={client} sx={{ mb: 4, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>{client}</Typography>
          {Object.entries(projects).map(([project, procs]) => (
            <Box key={project} sx={{ mb: 3, ml: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>{project}</Typography>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Process Name</TableCell>
                      <TableCell>Value of Process</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Timeline</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {procs.map((process) => (
                      <TableRow key={process.id}>
                        <TableCell>{process.name}</TableCell>
                        <TableCell>{process.value_of_process || '-'}</TableCell>
                        <TableCell>{process.description || '-'}</TableCell>
                        <TableCell>
                          {process.start_date && process.end_date ? (
                            `${process.start_date} to ${process.end_date}`
                          ) : (
                            'No timeline set'
                          )}
                        </TableCell>
                        <TableCell>
                          <span style={{
                            color: process.status === 'active' ? 'green' :
                              process.status === 'inactive' ? 'red' : 'orange'
                          }}>
                            {process.status}
                          </span>
                        </TableCell>
                        <TableCell>{process.owner || '-'}</TableCell>
                        <TableCell>
                          <Tooltip title="Edit Process">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/processes/edit/${process.id}`)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Process">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(process.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </Paper>
      ))}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const Processes = () => {
  return (
    <Routes>
      <Route path="/" element={<ProcessList />} />
      <Route path="/list" element={<ProcessList />} />
      <Route path="/add" element={<AddProcess />} />
      <Route path="/edit/:id" element={<EditProcess />} />
    </Routes>
  );
};

export default Processes;